/**
 * Inline Assignee Dropdown - Table Cell Component
 * A compact dropdown for assigning users directly in the table
 */

import { useState, useEffect } from 'react';
import { Select, MenuItem, SelectChangeEvent, Box, Avatar, Typography, CircularProgress } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { predictionsApi } from '../services/predictions.service';
import { Product, ExperimentVersion } from '../types';
import { getInitials, getAvatarColor } from '../utils/designSystem';

interface InlineAssigneeDropdownProps {
  predictionId: string;
  product: Product;
  experiment: ExperimentVersion;
  currentAssignee: string | null | undefined;
  availableAssignees: string[];
}

export default function InlineAssigneeDropdown({
  predictionId,
  product,
  experiment,
  currentAssignee,
  availableAssignees,
}: InlineAssigneeDropdownProps) {
  const [localValue, setLocalValue] = useState<string>(currentAssignee || '');
  const queryClient = useQueryClient();

  // Sync local state with prop changes
  useEffect(() => {
    setLocalValue(currentAssignee || '');
  }, [currentAssignee]);

  const updateAssigneeMutation = useMutation({
    mutationFn: (newAssignee: string | null) =>
      predictionsApi.updateAssignee(product, experiment, predictionId, newAssignee),
    onSuccess: () => {
      // Invalidate predictions list to refresh the table
      queryClient.invalidateQueries({ queryKey: ['predictions'] });
    },
    onError: (error) => {
      console.error('Failed to update assignee:', error);
      // Revert local value on error
      setLocalValue(currentAssignee || '');
    },
  });

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    
    // Update via API
    const assigneeValue = newValue === '' ? null : newValue;
    updateAssigneeMutation.mutate(assigneeValue);
  };

  // Prevent row click when interacting with dropdown
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Box onClick={handleClick} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Select
        value={localValue}
        onChange={handleChange}
        size="small"
        disabled={updateAssigneeMutation.isPending}
        displayEmpty
        sx={{
          minWidth: 150,
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          },
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography variant="body2" color="text.secondary">
                Unassigned
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: '0.75rem',
                  backgroundColor: getAvatarColor(selected),
                }}
              >
                {getInitials(selected)}
              </Avatar>
              <Typography variant="body2" color="text.primary">{selected}</Typography>
            </Box>
          );
        }}
      >
        <MenuItem value="">
          <em>Unassigned</em>
        </MenuItem>
        {availableAssignees.map((assignee) => (
          <MenuItem key={assignee} value={assignee}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: '0.75rem',
                  backgroundColor: getAvatarColor(assignee),
                }}
              >
                {getInitials(assignee)}
              </Avatar>
              <Typography variant="body2" color="text.primary">{assignee}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {updateAssigneeMutation.isPending && (
        <CircularProgress size={16} />
      )}
    </Box>
  );
}
